
import { Injectable } from '@angular/core';
import { VehicleUtils } from '../../utils/vehicle.utils';

export type ClaimFlowMode = 'full' | 'cid-upload';

@Injectable({
  providedIn: 'root'
})
export class WizardStepValidatorService {

  canEnterInitialOptions(isLanguageSelected: boolean): boolean {
    return isLanguageSelected;
  }

  canEnterDriverVehicle(
    isLanguageSelected: boolean,
    isLoginCompleted: boolean,
    crashesInfoLoaded: boolean,
    isFlowSelected: boolean
  ): boolean {
    return isLanguageSelected && isLoginCompleted && crashesInfoLoaded && isFlowSelected;
  }

  canEnterCidUpload(
    isLanguageSelected: boolean,
    isLoginCompleted: boolean,
    crashesInfoLoaded: boolean,
    isFlowSelected: boolean,
    claimFlowMode: ClaimFlowMode | null,
    isDriverVehicleValid: boolean
  ): boolean {
    return (
      claimFlowMode === 'cid-upload' &&
      isLanguageSelected &&
      isLoginCompleted &&
      crashesInfoLoaded &&
      isFlowSelected &&
      isDriverVehicleValid
    );
  }

  isCidUploadValid(cidDocuments: unknown[]): boolean {
    return cidDocuments.length > 0;
  }

  canEnterCidSubmit(
    isLanguageSelected: boolean,
    isLoginCompleted: boolean,
    crashesInfoLoaded: boolean,
    isFlowSelected: boolean,
    claimFlowMode: ClaimFlowMode | null,
    isDriverVehicleValid: boolean,
    isCidUploadValid: boolean
  ): boolean {
    return (
      claimFlowMode === 'cid-upload' &&
      isLanguageSelected &&
      isLoginCompleted &&
      crashesInfoLoaded &&
      isFlowSelected &&
      isDriverVehicleValid &&
      isCidUploadValid
    );
  }

  canEnterDettagliSinistro(
    isLanguageSelected: boolean,
    isLoginCompleted: boolean,
    crashesInfoLoaded: boolean,
    isDriverVehicleValid: boolean,
    claimFlowMode: ClaimFlowMode | null
  ): boolean {
    return (
      claimFlowMode === 'full' &&
      isLanguageSelected &&
      isLoginCompleted &&
      crashesInfoLoaded &&
      isDriverVehicleValid
    );
  }

  canEnterInvolvedVehicles(
    isLanguageSelected: boolean,
    isLoginCompleted: boolean,
    crashesInfoLoaded: boolean,
    isDriverVehicleValid: boolean,
    isDettagliSinistroValid: boolean,
    claimFlowMode: ClaimFlowMode | null
  ): boolean {
    return (
      claimFlowMode === 'full' &&
      isLanguageSelected &&
      isLoginCompleted &&
      crashesInfoLoaded &&
      isDriverVehicleValid &&
      isDettagliSinistroValid
    );
  }

  canEnterAccidentDynamics(
    isLanguageSelected: boolean,
    isLoginCompleted: boolean,
    crashesInfoLoaded: boolean,
    isDriverVehicleValid: boolean,
    isDettagliSinistroValid: boolean,
    isInvolvedVehiclesValid: boolean,
    claimFlowMode: ClaimFlowMode | null
  ): boolean {
    return (
      claimFlowMode === 'full' &&
      isLanguageSelected &&
      isLoginCompleted &&
      crashesInfoLoaded &&
      isDriverVehicleValid &&
      isDettagliSinistroValid &&
      isInvolvedVehiclesValid
    );
  }

  canEnterDanni(
    isLanguageSelected: boolean,
    isLoginCompleted: boolean,
    crashesInfoLoaded: boolean,
    isDriverVehicleValid: boolean,
    isDettagliSinistroValid: boolean,
    isInvolvedVehiclesValid: boolean,
    isAccidentDynamicsValid: boolean,
    claimFlowMode: ClaimFlowMode | null
  ): boolean {
    return (
      claimFlowMode === 'full' &&
      isLanguageSelected &&
      isLoginCompleted &&
      crashesInfoLoaded &&
      isDriverVehicleValid &&
      isDettagliSinistroValid &&
      isInvolvedVehiclesValid &&
      isAccidentDynamicsValid
    );
  }

  canEnterFoto(
    isLanguageSelected: boolean,
    isLoginCompleted: boolean,
    crashesInfoLoaded: boolean,
    isDriverVehicleValid: boolean,
    isDettagliSinistroValid: boolean,
    isInvolvedVehiclesValid: boolean,
    isAccidentDynamicsValid: boolean,
    isDanniValid: boolean,
    claimFlowMode: ClaimFlowMode | null
  ): boolean {
    return (
      claimFlowMode === 'full' &&
      isLanguageSelected &&
      isLoginCompleted &&
      crashesInfoLoaded &&
      isDriverVehicleValid &&
      isDettagliSinistroValid &&
      isInvolvedVehiclesValid &&
      isAccidentDynamicsValid &&
      isDanniValid
    );
  }

  canEnterRiepilogo(
    isLanguageSelected: boolean,
    isLoginCompleted: boolean,
    crashesInfoLoaded: boolean,
    isDriverVehicleValid: boolean,
    isDettagliSinistroValid: boolean,
    isInvolvedVehiclesValid: boolean,
    isAccidentDynamicsValid: boolean,
    isDanniValid: boolean,
    isFotoValid: boolean,
    claimFlowMode: ClaimFlowMode | null
  ): boolean {
    return (
      claimFlowMode === 'full' &&
      isLanguageSelected &&
      isLoginCompleted &&
      crashesInfoLoaded &&
      isDriverVehicleValid &&
      isDettagliSinistroValid &&
      isInvolvedVehiclesValid &&
      isAccidentDynamicsValid &&
      isDanniValid &&
      isFotoValid
    );
  }

  isFotoValid(vehiclePhotos: { [key: string]: any[] }, involvedVehicles: any[], vehicleLetters: string[]): boolean {
    const vehicleAHasPhotos = vehiclePhotos['A']?.length > 0;
    
    const allVehiclesHavePhotos = involvedVehicles.every((_, index) => {
      const vehicleLetter = vehicleLetters[index + 1];
      return vehiclePhotos[vehicleLetter]?.length > 0;
    });
    
    return vehicleAHasPhotos && (involvedVehicles.length === 0 || allVehiclesHavePhotos);
  }

  validateDriverForm(driverComponent: any, driverVehicleForm: any, crashesInfoData: any): boolean {
    const selectedDriverId = driverVehicleForm.get('selectedDriverId')?.value;
    const telefono = driverVehicleForm.get('telefono')?.value;

    if (!telefono || telefono.trim() === '') {
      return false;
    }

    if (driverComponent && !driverComponent.isPhoneValid) {
      return false;
    }

    if (!driverComponent || driverComponent.isLoadingDrivers) {
      return false;
    }

    if (
      selectedDriverId === null ||
      selectedDriverId === undefined ||
      selectedDriverId === '' ||
      selectedDriverId === VehicleUtils.NOT_AVAILABLE
    ) {
      return false;
    }

    // if (selectedDriverId === VehicleUtils.NOT_AVAILABLE) {
    //   return true; // consentito senza autista reale — rimosso
    // }

    if (driverComponent.availableDrivers.length > 0) {
      return driverComponent.availableDrivers.some(
        (d: { id: number }) =>
          d.id === Number(selectedDriverId) || d.id.toString() === String(selectedDriverId)
      );
    }

    const preDriverId = crashesInfoData?.driverInfo?.driverId;
    return (
      preDriverId != null &&
      preDriverId !== '' &&
      (Number(preDriverId) === Number(selectedDriverId) ||
        String(preDriverId) === String(selectedDriverId))
    );
  }

  getCompletedSteps(
    claimFlowMode: ClaimFlowMode | null,
    isLanguageSelected: boolean,
    isFlowSelected: boolean,
    isDriverVehicleValid: boolean,
    isDettagliSinistroValid: boolean,
    isInvolvedVehiclesValid: boolean,
    isAccidentDynamicsValid: boolean,
    isDanniValid: boolean,
    isFotoValid: boolean,
    isCidUploadValid: boolean
  ): number {
    if (claimFlowMode === 'cid-upload') {
      let completed = 0;
      if (isFlowSelected) completed++;
      if (isDriverVehicleValid) completed++;
      if (isCidUploadValid) completed++;
      return completed;
    }

    let completed = 0;
    if (isLanguageSelected) completed++;
    if (isDriverVehicleValid) completed++;
    if (isDettagliSinistroValid) completed++;
    if (isInvolvedVehiclesValid) completed++;
    if (isAccidentDynamicsValid) completed++;
    if (isDanniValid) completed++;
    if (isFotoValid) completed++;
    return completed;
  }

  getTotalSteps(claimFlowMode: ClaimFlowMode | null): number {
    return claimFlowMode === 'cid-upload' ? 3 : 7;
  }

  getProgressPercentage(completedSteps: number, totalSteps: number): number {
    return (completedSteps / totalSteps) * 100;
  }
}